/**
 * DangerousGoodsPage - Hazardous Materials Register
 * 
 * Features:
 * - List all dangerous goods with filtering by site, DG class
 * - Add/edit dangerous goods items
 * - SDS expiry tracking with alerts
 * - Storage location management
 * - Compliance status indicators
 */
import React, { useState, useEffect } from 'react';
import api, { formatDate } from '../lib/api';
import { DG_CLASSES } from '../lib/constants';
import { Card, Spinner, EmptyState, Select } from '../components/shared';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { AlertTriangle, Plus, MapPin, Calendar, FileText, Package, Edit2, X, Check, AlertCircle } from 'lucide-react';

// Status badge for DG items
const ComplianceBadge = ({ item }) => {
  const now = new Date();
  const sdsExpiry = item.sds_expiry ? new Date(item.sds_expiry) : null;
  const nextReview = item.next_review_at ? new Date(item.next_review_at) : null;
  
  // Check if SDS is expired or expiring soon (30 days)
  const sdsExpired = sdsExpiry && sdsExpiry < now;
  const sdsExpiringSoon = sdsExpiry && !sdsExpired && (sdsExpiry - now) < 30 * 24 * 60 * 60 * 1000;
  
  // Check if review is overdue
  const reviewOverdue = nextReview && nextReview < now;
  
  if (sdsExpired) {
    return <span className="compliance-badge compliance-critical" data-testid="compliance-expired">SDS Expired</span>;
  }
  if (sdsExpiringSoon) {
    return <span className="compliance-badge compliance-warning" data-testid="compliance-expiring">SDS Expiring</span>;
  }
  if (reviewOverdue) {
    return <span className="compliance-badge compliance-warning" data-testid="compliance-review">Review Due</span>;
  }
  return <span className="compliance-badge compliance-ok" data-testid="compliance-ok">Compliant</span>;
};

// DG Class badge
const DGClassBadge = ({ dgClass }) => {
  const config = DG_CLASSES[dgClass] || { label: `Class ${dgClass}`, color: '#6b7280' };
  return (
    <span className="dg-class-badge" style={{ borderColor: config.color, color: config.color }} data-testid={`dg-class-${dgClass}`}>
      <AlertTriangle size={14} />
      <span>{dgClass}</span>
    </span>
  );
};

// Add/Edit Modal
const DGFormModal = ({ isOpen, onClose, onSave, item, sites }) => {
  const [form, setForm] = useState({
    site_id: '',
    un_number: '',
    proper_shipping_name: '',
    dg_class: '',
    subsidiary_risk: '',
    packing_group: '',
    quantity: '',
    unit: 'L',
    storage_location: '',
    hazchem_code: '',
    emergency_contacts: '',
    first_response_notes: '',
    placard_required: false,
    spill_kit_required: true,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (item) {
      setForm({
        site_id: item.site_id || '',
        un_number: item.un_number || '',
        proper_shipping_name: item.proper_shipping_name || '',
        dg_class: item.dg_class || '',
        subsidiary_risk: item.subsidiary_risk || '',
        packing_group: item.packing_group || '',
        quantity: item.quantity || '',
        unit: item.unit || 'L',
        storage_location: item.storage_location || '',
        hazchem_code: item.hazchem_code || '',
        emergency_contacts: item.emergency_contacts || '',
        first_response_notes: item.first_response_notes || '',
        placard_required: item.placard_required || false,
        spill_kit_required: item.spill_kit_required ?? true,
      });
    } else {
      setForm({
        site_id: sites[0]?.id || '',
        un_number: '',
        proper_shipping_name: '',
        dg_class: '3',
        subsidiary_risk: '',
        packing_group: '',
        quantity: '',
        unit: 'L',
        storage_location: '',
        hazchem_code: '',
        emergency_contacts: '',
        first_response_notes: '',
        placard_required: false,
        spill_kit_required: true,
      });
    }
  }, [item, sites]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.proper_shipping_name || !form.dg_class || !form.site_id) {
      toast.error('Please fill in required fields');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err) {
      toast.error(err.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="modal-overlay" data-testid="dg-form-modal">
      <div className="modal-content modal-lg">
        <div className="modal-header">
          <h2>{item ? 'Edit Dangerous Good' : 'Add Dangerous Good'}</h2>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-grid">
              <div className="form-group">
                <label className="form-label required">Site</label>
                <select 
                  className="form-select" 
                  value={form.site_id} 
                  onChange={(e) => setForm({...form, site_id: e.target.value})}
                  data-testid="dg-site-select"
                >
                  {sites.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">UN Number</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. UN1203"
                  value={form.un_number}
                  onChange={(e) => setForm({...form, un_number: e.target.value})}
                  data-testid="dg-un-number"
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label required">Proper Shipping Name</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. Petrol/Gasoline"
                  value={form.proper_shipping_name}
                  onChange={(e) => setForm({...form, proper_shipping_name: e.target.value})}
                  data-testid="dg-shipping-name"
                />
              </div>
              <div className="form-group">
                <label className="form-label required">DG Class</label>
                <select 
                  className="form-select" 
                  value={form.dg_class}
                  onChange={(e) => setForm({...form, dg_class: e.target.value})}
                  data-testid="dg-class-select"
                >
                  {Object.entries(DG_CLASSES).map(([k, v]) => (
                    <option key={k} value={k}>{v.label}</option>
                  ))}
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Subsidiary Risk</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. 6.1"
                  value={form.subsidiary_risk}
                  onChange={(e) => setForm({...form, subsidiary_risk: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-label">Packing Group</label>
                <select 
                  className="form-select" 
                  value={form.packing_group}
                  onChange={(e) => setForm({...form, packing_group: e.target.value})}
                >
                  <option value="">Select...</option>
                  <option value="I">I - High Danger</option>
                  <option value="II">II - Medium Danger</option>
                  <option value="III">III - Low Danger</option>
                </select>
              </div>
              <div className="form-group">
                <label className="form-label">Quantity</label>
                <div className="input-with-unit">
                  <input 
                    type="text" 
                    className="form-input" 
                    placeholder="e.g. 200"
                    value={form.quantity}
                    onChange={(e) => setForm({...form, quantity: e.target.value})}
                    data-testid="dg-quantity"
                  />
                  <select 
                    className="form-select unit-select"
                    value={form.unit}
                    onChange={(e) => setForm({...form, unit: e.target.value})}
                  >
                    <option value="L">L</option>
                    <option value="kg">kg</option>
                    <option value="mL">mL</option>
                    <option value="g">g</option>
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Hazchem Code</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. 3YE"
                  value={form.hazchem_code}
                  onChange={(e) => setForm({...form, hazchem_code: e.target.value})}
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">Storage Location</label>
                <input 
                  type="text" 
                  className="form-input" 
                  placeholder="e.g. Flammable liquids cabinet - Building A"
                  value={form.storage_location}
                  onChange={(e) => setForm({...form, storage_location: e.target.value})}
                  data-testid="dg-storage-location"
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">Emergency Contacts</label>
                <textarea 
                  className="form-textarea" 
                  rows={2}
                  placeholder="Emergency contact numbers and names"
                  value={form.emergency_contacts}
                  onChange={(e) => setForm({...form, emergency_contacts: e.target.value})}
                />
              </div>
              <div className="form-group form-group-full">
                <label className="form-label">First Response Notes</label>
                <textarea 
                  className="form-textarea" 
                  rows={2}
                  placeholder="Initial response procedures for spills/incidents"
                  value={form.first_response_notes}
                  onChange={(e) => setForm({...form, first_response_notes: e.target.value})}
                />
              </div>
              <div className="form-group">
                <label className="form-checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={form.placard_required}
                    onChange={(e) => setForm({...form, placard_required: e.target.checked})}
                  />
                  <span>Placard Required</span>
                </label>
              </div>
              <div className="form-group">
                <label className="form-checkbox-label">
                  <input 
                    type="checkbox" 
                    checked={form.spill_kit_required}
                    onChange={(e) => setForm({...form, spill_kit_required: e.target.checked})}
                  />
                  <span>Spill Kit Required</span>
                </label>
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" variant="primary" disabled={saving} data-testid="dg-save-btn">
              {saving ? 'Saving...' : (item ? 'Update' : 'Add Item')}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Detail Modal
const DGDetailModal = ({ isOpen, onClose, item, onEdit }) => {
  if (!isOpen || !item) return null;

  return (
    <div className="modal-overlay" data-testid="dg-detail-modal">
      <div className="modal-content modal-lg">
        <div className="modal-header">
          <div className="modal-header-content">
            <DGClassBadge dgClass={item.dg_class} />
            <h2>{item.proper_shipping_name}</h2>
          </div>
          <button className="modal-close" onClick={onClose}><X size={20} /></button>
        </div>
        <div className="modal-body">
          <div className="detail-grid">
            <div className="detail-section">
              <h3>Identification</h3>
              <div className="detail-row">
                <span className="detail-label">UN Number</span>
                <span className="detail-value">{item.un_number || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">DG Class</span>
                <span className="detail-value">{DG_CLASSES[item.dg_class]?.label || item.dg_class}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Subsidiary Risk</span>
                <span className="detail-value">{item.subsidiary_risk || 'None'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Packing Group</span>
                <span className="detail-value">{item.packing_group || 'N/A'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Hazchem Code</span>
                <span className="detail-value">{item.hazchem_code || 'N/A'}</span>
              </div>
            </div>
            
            <div className="detail-section">
              <h3>Storage</h3>
              <div className="detail-row">
                <span className="detail-label">Site</span>
                <span className="detail-value">{item.site_name}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Location</span>
                <span className="detail-value">{item.storage_location || 'Not specified'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Quantity</span>
                <span className="detail-value">{item.quantity || 'N/A'}</span>
              </div>
            </div>

            <div className="detail-section">
              <h3>Compliance</h3>
              <div className="detail-row">
                <span className="detail-label">Status</span>
                <ComplianceBadge item={item} />
              </div>
              <div className="detail-row">
                <span className="detail-label">SDS Expiry</span>
                <span className="detail-value">{item.sds_expiry ? formatDate(item.sds_expiry) : 'Not set'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Next Review</span>
                <span className="detail-value">{item.next_review_at ? formatDate(item.next_review_at) : 'Not set'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Placard Required</span>
                <span className="detail-value">{item.placard_required ? 'Yes' : 'No'}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Spill Kit Required</span>
                <span className="detail-value">{item.spill_kit_required ? 'Yes' : 'No'}</span>
              </div>
            </div>

            {item.emergency_contacts && (
              <div className="detail-section detail-section-full">
                <h3>Emergency Contacts</h3>
                <p className="detail-text">{item.emergency_contacts}</p>
              </div>
            )}

            {item.first_response_notes && (
              <div className="detail-section detail-section-full">
                <h3>First Response Notes</h3>
                <p className="detail-text">{item.first_response_notes}</p>
              </div>
            )}
          </div>
        </div>
        <div className="modal-footer">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button variant="primary" onClick={() => onEdit(item)} data-testid="dg-edit-btn">
            <Edit2 size={16} /> Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

// Main Page Component
const DangerousGoodsPage = () => {
  const [items, setItems] = useState([]);
  const [sites, setSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({ site_id: '', dg_class: '' });
  const [showFormModal, setShowFormModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);

  useEffect(() => {
    loadData();
  }, [filters]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load sites first
      const sitesRes = await api.get('/sites');
      setSites(sitesRes.items || []);

      // Load DG items with filters
      let url = '/dangerous-goods';
      const params = new URLSearchParams();
      if (filters.site_id) params.append('site_id', filters.site_id);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await api.get(url);
      let dgItems = res.items || [];
      
      // Filter by DG class on frontend (backend doesn't support this filter)
      if (filters.dg_class) {
        dgItems = dgItems.filter(item => item.dg_class === filters.dg_class);
      }
      
      setItems(dgItems);
    } catch (err) {
      console.error('Failed to load dangerous goods:', err);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (formData) => {
    if (selectedItem) {
      // Update existing item
      await api.patch(`/dangerous-goods/${selectedItem.id}`, formData);
      toast.success('Dangerous good updated');
    } else {
      // Create new item
      await api.post('/dangerous-goods', formData);
      toast.success('Dangerous good added');
    }
    loadData();
    setSelectedItem(null);
  };

  const handleAdd = () => {
    setSelectedItem(null);
    setShowFormModal(true);
  };

  const handleEdit = (item) => {
    setSelectedItem(item);
    setShowDetailModal(false);
    setShowFormModal(true);
  };

  const handleView = (item) => {
    setSelectedItem(item);
    setShowDetailModal(true);
  };

  // Calculate summary stats
  const stats = {
    total: items.length,
    expiredSDS: items.filter(item => {
      const sdsExpiry = item.sds_expiry ? new Date(item.sds_expiry) : null;
      return sdsExpiry && sdsExpiry < new Date();
    }).length,
    reviewDue: items.filter(item => {
      const nextReview = item.next_review_at ? new Date(item.next_review_at) : null;
      return nextReview && nextReview < new Date();
    }).length,
  };

  return (
    <div className="page dg-page" data-testid="dg-page">
      <div className="page-header">
        <div>
          <h1>Dangerous Goods Register</h1>
          <p>Track hazardous materials, SDS documents, and compliance</p>
        </div>
        <Button variant="primary" onClick={handleAdd} data-testid="add-dg-btn">
          <Plus size={16} /> Add Item
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="page-stats" data-testid="dg-stats">
        <div className="stat-card">
          <Package size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.total}</span>
            <span className="stat-label">Total Items</span>
          </div>
        </div>
        <div className={`stat-card ${stats.expiredSDS > 0 ? 'stat-danger' : ''}`}>
          <FileText size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.expiredSDS}</span>
            <span className="stat-label">Expired SDS</span>
          </div>
        </div>
        <div className={`stat-card ${stats.reviewDue > 0 ? 'stat-warning' : ''}`}>
          <Calendar size={20} className="stat-icon" />
          <div className="stat-content">
            <span className="stat-value">{stats.reviewDue}</span>
            <span className="stat-label">Review Due</span>
          </div>
        </div>
      </div>

      {/* Filters */}
      <Card className="filters-card">
        <div className="filters-row">
          <Select
            label="Site"
            value={filters.site_id}
            onChange={(e) => setFilters({ ...filters, site_id: e.target.value })}
            options={[
              { value: '', label: 'All Sites' },
              ...sites.map(s => ({ value: s.id, label: s.name }))
            ]}
          />
          <Select
            label="DG Class"
            value={filters.dg_class}
            onChange={(e) => setFilters({ ...filters, dg_class: e.target.value })}
            options={[
              { value: '', label: 'All Classes' },
              ...Object.entries(DG_CLASSES).map(([k, v]) => ({ value: k, label: v.label }))
            ]}
          />
          {(filters.site_id || filters.dg_class) && (
            <button className="filter-clear-btn" onClick={() => setFilters({ site_id: '', dg_class: '' })}>
              Clear filters
            </button>
          )}
        </div>
      </Card>

      {/* Items List */}
      {loading ? (
        <div className="page-loading"><Spinner /></div>
      ) : items.length === 0 ? (
        <EmptyState
          icon={<AlertTriangle size={48} />}
          title="No dangerous goods found"
          description={filters.site_id || filters.dg_class ? "No items match the current filters" : "Add your first hazardous material to get started"}
        />
      ) : (
        <Card className="table-card">
          <table className="data-table" data-testid="dg-table">
            <thead>
              <tr>
                <th>Class</th>
                <th>Name</th>
                <th>UN #</th>
                <th>Site</th>
                <th>Location</th>
                <th>Quantity</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map(item => (
                <tr 
                  key={item.id} 
                  onClick={() => handleView(item)}
                  className="table-row clickable"
                  data-testid={`dg-row-${item.id}`}
                >
                  <td><DGClassBadge dgClass={item.dg_class} /></td>
                  <td className="cell-title">{item.proper_shipping_name}</td>
                  <td><code>{item.un_number || '-'}</code></td>
                  <td>{item.site_name}</td>
                  <td className="cell-location">
                    <MapPin size={14} />
                    <span>{item.storage_location || 'Not specified'}</span>
                  </td>
                  <td>{item.quantity || '-'}</td>
                  <td><ComplianceBadge item={item} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Modals */}
      <DGFormModal
        isOpen={showFormModal}
        onClose={() => { setShowFormModal(false); setSelectedItem(null); }}
        onSave={handleSave}
        item={selectedItem}
        sites={sites}
      />
      <DGDetailModal
        isOpen={showDetailModal}
        onClose={() => { setShowDetailModal(false); setSelectedItem(null); }}
        item={selectedItem}
        onEdit={handleEdit}
      />
    </div>
  );
};

export default DangerousGoodsPage;
